import type {
  AuthoringDeclaration,
  AuthoringDocument,
  AuthoringTarget,
  SourcePoint,
} from "./types.js";
import type { AssetKind, ScriptV3 } from "../../script/v3/types.js";

const GENERATED_SOURCE: SourcePoint = { line: 1, column: 1 };

export function scriptDeclarations(script: ScriptV3): AuthoringDeclaration[] {
  const declarations: AuthoringDeclaration[] = [];
  for (const actor of script.actors) {
    const value = structuredClone(actor);
    value.states = [];
    declarations.push({
      declarationKind: "actor",
      value,
      source: GENERATED_SOURCE,
    });
    for (const state of actor.states) declarations.push(stateDeclaration("actor", actor.actor_id, state));
  }
  for (const location of script.locations) {
    const value = structuredClone(location);
    value.states = [];
    declarations.push({
      declarationKind: "location",
      value,
      source: GENERATED_SOURCE,
    });
    for (const state of location.states) declarations.push(stateDeclaration("location", location.location_id, state));
  }
  for (const prop of script.props) {
    const value = structuredClone(prop);
    value.states = [];
    declarations.push({
      declarationKind: "prop",
      value,
      source: GENERATED_SOURCE,
    });
    for (const state of prop.states) declarations.push(stateDeclaration("prop", prop.prop_id, state));
  }
  return declarations;
}

function stateDeclaration(
  ownerKind: AssetKind,
  ownerId: string,
  state: ScriptV3["actors"][number]["states"][number],
): AuthoringDeclaration {
  return {
    declarationKind: "state",
    ownerKind,
    ownerId,
    value: structuredClone(state),
    source: GENERATED_SOURCE,
  };
}

export function scriptExportDocument(script: ScriptV3): AuthoringDocument {
  const target: Extract<AuthoringTarget, { kind: "script" }> = {
    kind: "script",
    title: script.title,
    worldview: script.worldview,
    style: script.style,
    episodes: structuredClone(script.episodes),
    source: GENERATED_SOURCE,
  };
  if (script.worldview_raw !== undefined) target.worldviewRaw = script.worldview_raw;
  if (script.synopsis !== undefined) target.synopsis = script.synopsis;
  if (script.logline !== undefined) target.logline = script.logline;
  if (script.theme !== undefined) target.theme = script.theme;
  if (script.main_characters !== undefined) target.mainCharacters = structuredClone(script.main_characters);
  if (script.extend !== undefined) target.extend = structuredClone(script.extend);
  return {
    version: 1,
    declarations: scriptDeclarations(script),
    targets: [target],
    targetKind: "script",
  };
}

export function targetExportDocument<T extends AuthoringTarget>(
  targetKind: T["kind"],
  targets: T[],
): AuthoringDocument {
  return {
    version: 1,
    declarations: [],
    targets,
    targetKind,
  };
}

export { GENERATED_SOURCE };
